import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { logoutUser as logoutUserService } from "@/lib/services/auth";
import { getEmploymentVerificationStatus } from "@/lib/services";
import { queryClient } from "@/lib/queryClient";

interface User {
  username: string,
  loggedIn: boolean,
  email_verified?: boolean
}

interface AuthContextType {
  user: User | null;
  isVerified: boolean;
  login: (userData: User) => void;
  logout: () => void;
  refreshVerification: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? (JSON.parse(stored) as User) : null;
    } catch {
      return null;
    }
  });
  const [isVerified, setIsVerified] = useState(false);

  const fetchVerification = useCallback(async () => {
    try {
      const status = await getEmploymentVerificationStatus();
      setIsVerified(status.status === "verified");
    } catch {
      setIsVerified(false);
    }
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    try {
      localStorage.setItem("user", JSON.stringify(userData));
    } catch {
      // localStorage unavailable (e.g. Safari private mode)
    }
  };

  const logout = useCallback(async () => {
    await logoutUserService();
    setUser(null);
    setIsVerified(false);
    queryClient.clear();
    try {
      localStorage.removeItem("user");
    } catch {
      // localStorage unavailable (e.g. Safari private mode)
    }
  }, []);

  const refreshVerification = useCallback(async () => {
    if (user) await fetchVerification();
  }, [user, fetchVerification]);

  // Fetch verification status whenever user changes
  useEffect(() => {
    if (user) {
      fetchVerification();
    } else {
      setIsVerified(false);
    }
  }, [user, fetchVerification]);

  return (
    <AuthContext.Provider value={{ user, isVerified, login, logout, refreshVerification }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
